//@flow
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import {makeStyles} from '@material-ui/core/styles';
import {
  rgxCountries,
  getFlagByCountryCode,
} from '../../modules/on-boarding/utils';
import CustomTextField from '../CustomTextField/CustomTextField';
import {connect} from 'react-redux';

import type {Country} from '../../entities';
import type {RootState} from '../../modules';

export default connect(
  (state: RootState) => ({
    countries: state.country.countries,
  }),
  null
)(TelInput);
type Props = {
  countries?: Country[],
  label: string,
  variant?: string,
  fullWidth?: boolean,
  name: string,
  error?: boolean,
  helperText?: string,
  inputRef?: any,
  // register: (rules?: ValidationRules) => (ref: Ref | null) => void,
  onChange?: (phone: string) => void,
  onError?: (error: string) => void,
  defaultValue: ?string,
  country?: Country,
  disabled?: boolean,
  className?: string,
};

const useStyles = makeStyles(theme => ({
  mainWrap: {
    position: 'relative',
  },
  flagIcon: {
    width: 12,
    height: 12,
    marginRight: 6,
  },
  textField: {
    '& label': {
      paddingLeft: theme.spacing(10),
    },
    '& legend': {
      marginLeft: 59,
    },
  },
  select: {
    marginTop: '6px',
    position: 'absolute',
    left: 10,
    top: 5,
    height: 56,

    '& div': {
      backgroundColor: 'unset!important',
    },
  },
  label: {
    marginLeft: '-80px !important',
  },
  input: {
    paddingLeft: theme.spacing(12),
  },
}));

function TelInput(props: Props) {
  const {
    countries,
    label,
    fullWidth,
    name,
    error,
    inputRef,
    helperText,
    onChange,
    defaultValue,
    country,
    disabled = false,
    className,
  } = props;

  const classes = useStyles();
  const initialSelectedCountry = useMemo(() => {
    if (defaultValue && countries) {
      const foundCountry = countries.find(
        c =>
          !!defaultValue &&
          c.phonePrefix &&
          defaultValue.startsWith(c.phonePrefix)
      );
      return foundCountry || countries[0];
    }
    if (country) return country;
    return (
      countries?.find(c => c.code === 'PE') || {
        code: 'PE',
        phonePrefix: '51',
        name: 'Peru',
      }
    );
  }, [countries, country, defaultValue]);

  const initialDefaultValue = useMemo(
    () =>
      defaultValue && initialSelectedCountry?.phonePrefix
        ? defaultValue.substring(initialSelectedCountry.phonePrefix.length)
        : defaultValue || '',
    [defaultValue, initialSelectedCountry]
  );

  const [_value, _setValue] = useState(initialDefaultValue);
  const [_error, _setError] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(
    initialSelectedCountry
  );

  const countryPrefix = selectedCountry?.phonePrefix || '51';

  useEffect(() => {
    if (initialSelectedCountry && !defaultValue) {
      setSelectedCountry(initialSelectedCountry);
    }
  }, [defaultValue, initialSelectedCountry]);

  const proxyOnChange = e => {
    const v = e.target.value;
    if (countryPrefix) validateFields(v, countryPrefix);
    _setValue(v);
  };

  // only numbers
  const validType = (v: string): ?boolean => {
    const numberRgx = /^[0-9]*$/;
    return numberRgx.test(v);
  };

  const validFormat = useCallback(
    (v: string, prefix: string): ?boolean => {
      const countrySelected = countries
        ? countries.find(i => i.phonePrefix === prefix)
        : null;

      if (countrySelected && rgxCountries[countrySelected.code])
        return rgxCountries[countrySelected.code].test(v);
      else return rgxCountries['DEFAULT'].test(v);
    },
    [countries]
  );

  const validateFields = (v: string, p: string) => {
    if (!validType(v)) _setError('Formato de número inválido');
    else if (!validFormat(v, p)) _setError('Formato de número inválido');
    else if (!v) _setError('required');
    else _setError('');
  };

  const handleChangeCountry = event => {
    const countryCode = event.target.value;

    const newSelectedCountry = countries?.find(
      country => country.code === countryCode
    );

    if (newSelectedCountry) {
      setSelectedCountry(newSelectedCountry);
      validateFields(_value, newSelectedCountry.phonePrefix);
    }
  };

  useEffect(() => {
    if (!!_value && !_error && onChange && countryPrefix) {
      onChange(`${countryPrefix}${_value}`);
    }
  }, [_value, _error, onChange, countryPrefix]);

  if (!countries) return null;

  return (
    <div className={`${classes.mainWrap} ${className ? className : ''}`}>
      <CustomTextField
        label={<div className={classes.label}>{label} </div>}
        fullWidth={fullWidth}
        name={name}
        autoComplete={name}
        error={!!_error || error}
        helperText={helperText ? helperText : _error ? _error : ''}
        onChange={proxyOnChange}
        value={_value}
        // defaultValue={_value}
        inputProps={{className: classes.input}}
        inputRef={inputRef}
        InputLabelProps={{
          shrink: true,
        }}
        className={classes.textField}
        disabled={disabled}
      />
      <Select
        labelId="country"
        id="country"
        value={selectedCountry?.code || 'PE'}
        onChange={handleChangeCountry}
        className={classes.select}
        disableUnderline
      >
        {countries.map(c => (
          <MenuItem key={c.code} value={c.code}>
            <img
              src={getFlagByCountryCode(c.code)}
              className={classes.flagIcon}
              alt={c.name}
            />
            {selectedCountry?.code !== c.code && c.name} ({c.phonePrefix})
          </MenuItem>
        ))}
      </Select>
    </div>
  );
}
